import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../config/api';

interface UserInfo {
  user_email: string;
  user_uuid: string;
  cognito_uuid: string;
  user: string;
  [key: string]: any;
}

interface AuthContextType {
  authToken: string;
  setAuthToken: (token: string) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (auth: boolean) => void;
  userInfo: UserInfo | null;
  setUserInfo: (info: UserInfo | null) => void;
  handleAuthenticate: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  // Load token from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    if (savedToken) {
      setAuthToken(savedToken);
    }
  }, []);

  // Save token to localStorage when it changes
  useEffect(() => {
    if (authToken) {
      localStorage.setItem('authToken', authToken);
    } else {
      localStorage.removeItem('authToken');
    }
  }, [authToken]);

  const handleAuthenticate = async () => {
    if (!authToken.trim()) {
      alert('Please enter a JWT token');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/tools/api/v1/terrain-pro/auth-info`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUserInfo({
          user_email: data.user || '',
          user_uuid: data.cognito_uuid || '',
          cognito_uuid: data.cognito_uuid || '',
          user: data.user || '',
          ...data
        });
        alert(`✓ Authentication successful!\n\nUser: ${data.user || 'N/A'}\nUUID: ${data.cognito_uuid || 'N/A'}`);
      } else {
        setIsAuthenticated(false);
        setUserInfo(null);
        const error = await response.text();
        alert(`✗ Authentication failed!\n\nStatus: ${response.status}\nError: ${error}`);
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUserInfo(null);
      console.error('Authentication error:', error);
      alert(`✗ Authentication failed!\n\nError: ${error instanceof Error ? error.message : 'Network error'}`);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        authToken,
        setAuthToken,
        isAuthenticated,
        setIsAuthenticated,
        userInfo,
        setUserInfo,
        handleAuthenticate
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};


